import { X } from "@/components/ui/icons";
import { type ComponentProps, useId, useRef } from "react";
import { Button } from "react-aria-components/Button";
import { Dialog } from "react-aria-components/Dialog";
import { Heading } from "react-aria-components/Heading";
import { Modal, ModalOverlay } from "react-aria-components/Modal";
import { StyleSheet, View } from "react-native";

import { useIsDesktop } from "@/hooks/useResponsive";
import { cn } from "@/lib/ui/cn";
import { useCloseOnUnhandledEscape } from "@/lib/ui/webKeyboard";
import { useThemeColors } from "@/theme/theme";
import { Sheet as NativeSheet } from "./SheetBase";
import "./controls.web.css";

export { SheetList, SheetScroll, SheetTitle } from "./SheetBase";

export function Sheet(props: ComponentProps<typeof NativeSheet>) {
  const desktop = useIsDesktop();
  if (!desktop || props.hosted) return <NativeSheet {...props} />;
  return <DesktopDialog {...props} />;
}

function DesktopDialog(
  {
    visible = true,
    onClose,
    children,
    label,
    title,
    subtitle,
    footer,
    className,
    style,
    testID,
    backdropLabel,
    closeButton,
  }: ComponentProps<typeof NativeSheet>,
) {
  const c = useThemeColors();
  const descriptionId = useId();
  const panel = useRef<HTMLElement>(null);
  useCloseOnUnhandledEscape(panel, onClose, visible);
  const panelStyle = StyleSheet.flatten(style);
  return (
    <ModalOverlay
      isOpen={visible}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      isDismissable
      className="desktop-sheet-overlay"
    >
      <Modal>
        <Dialog
          ref={panel}
          aria-describedby={subtitle ? descriptionId : undefined}
          className={cn(
            "desktop-sheet-panel w-full max-w-[560px] rounded-xl border border-border bg-surface px-6 py-6 outline-none",
            className,
          )}
          style={{
            width: cssDimension(panelStyle?.width),
            height: cssDimension(panelStyle?.height),
            maxHeight: cssDimension(panelStyle?.maxHeight),
            paddingBottom: cssDimension(panelStyle?.paddingBottom),
          }}
          data-testid={testID}
        >
          <div className={title || closeButton ? "desktop-sheet-header" : "desktop-sr-only"}>
            <div>
              <Heading slot="title" className={title ? "desktop-sheet-title" : "desktop-sr-only"}>
                {title ?? label ?? "Dialog"}
              </Heading>
              {subtitle
                ? (
                  <p id={descriptionId} className="desktop-sheet-description">
                    {subtitle}
                  </p>
                )
                : null}
            </div>
            {title || closeButton
              ? (
                <Button
                  slot="close"
                  className="desktop-sheet-close"
                  aria-label={backdropLabel ?? `Close ${title ?? label ?? "dialog"}`}
                >
                  <X size={18} color={c.subtle} />
                </Button>
              )
              : null}
          </div>
          {footer ? <View className="min-h-0 shrink">{children}</View> : children}
          {footer ? <View className="desktop-sheet-footer mt-5">{footer}</View> : null}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function cssDimension(
  value: number | string | object | null | undefined,
): number | string | undefined {
  return typeof value === "number" || typeof value === "string" ? value : undefined;
}
